import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Params, useParams } from 'react-router-dom';
import { CompletedHandoutGroup, ItemQuantity } from '../models/warehouse-models';
import { ConsumedResourceDocument, TableComponentProps } from '../models/table-models';
import {
    getQuantityCount,
    useGetWarehouse,
    useGetAllCompletedHandouts,
    useGetAllQuantities,
    useGetConsumedResources,
    useGetPassportTypesForWarehouse,
} from '../utils/warehouses-hooks';
import { useTableData } from '../utils/data-hooks';
import { usePagination } from '../utils/pagination-hooks';
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import StepWizard from 'react-step-wizard';
import { AddEditQuantity } from './stock/AddEditQuantity';
import { AddActionComponent } from '../components/AddActionComponent';
import { Role } from '../utils/Enums/Roles';
import { QuantitiesTable } from './stock/QuantitiesTable';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { CustomStepWizardNav } from '../components/CustomStepWizardNav';
import { CompletedHandoutsTable } from './stock/CompletedHandoutsTable';
import { AddMultiHandout } from './stock/AddMultiHandout';
import { WarehousePdfImport } from './warehouses/WarehousePdfImport';
import { ManualWarehouseQuantityImport } from './warehouses/ManualWarehouseQuantityImport';
import { ConsumedResourcesTable } from './warehouses/ConsumedResourcesTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';

interface WarehouseViewQueryParams extends Params {
    id: string;
}

type RefetchRef = React.MutableRefObject<() => void>;

interface WarehouseTabLoaderProps {
    filter: TableComponentProps['filter'];
    parentPagination: TableComponentProps['pagination'];
    warehouseId: string;
    refetchRef: RefetchRef;
}

const QuantitiesTabLoader = ({
    filter,
    parentPagination,
    warehouseId,
    warehouseType,
    refetchRef,
    setShowModal,
    setSelectedItem,
}: WarehouseTabLoaderProps & {
    warehouseType?: number;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedItem: React.Dispatch<React.SetStateAction<ItemQuantity | undefined>>;
}) => {
    const tabPagination = usePagination(parentPagination.pageSize);
    const { setCurrentPage, setPagination } = tabPagination;
    const [getQuantities, fetchNewQuantities] = useTableData<ItemQuantity[]>(
        useGetAllQuantities,
        { ...tabPagination, pageSize: parentPagination.pageSize },
        {
            ...filter,
            id: warehouseId,
        }
    );

    const refetchNewQuantities = useCallback(async () => {
        resourceCache(useGetAllQuantities).clear();
        resourceCache(getQuantityCount).clear();
        resourceCache(useGetPassportTypesForWarehouse).clear();
        await fetchNewQuantities({
            'pager.page': tabPagination.currentPage,
            'pager.itemsPerPage': tabPagination.pageSize,
            pagination: tabPagination,
            filter: {
                ...filter,
                id: warehouseId,
            },
        });
    }, [fetchNewQuantities, filter, tabPagination, warehouseId]);

    useEffect(() => {
        refetchRef.current = refetchNewQuantities;
        return () => {
            refetchRef.current = () => {
                resourceCache(useGetAllQuantities).clear();
            };
        };
    }, [refetchNewQuantities, refetchRef]);

    useEffect(() => {
        setCurrentPage(1);
        setPagination({ ...parentPagination });
    }, [parentPagination, setCurrentPage, setPagination]);

    return (
        <QuantitiesTable
            getEntities={getQuantities}
            pagination={tabPagination}
            refetchEntitiesHook={refetchNewQuantities}
            warehouseId={+warehouseId}
            warehouseType={warehouseType}
            setShowModal={setShowModal}
            setSelectedItem={setSelectedItem}
        />
    );
};

const ConsumedResourcesTabLoader = ({ filter, parentPagination, warehouseId, refetchRef }: WarehouseTabLoaderProps) => {
    const tabPagination = usePagination(parentPagination.pageSize);
    const { setCurrentPage, setPagination } = tabPagination;
    const [getConsumedResources, fetchNewConsumedResources] = useTableData<ConsumedResourceDocument[]>(
        useGetConsumedResources,
        { ...tabPagination, pageSize: parentPagination.pageSize },
        {
            ...filter,
            id: warehouseId,
        }
    );

    const refetchConsumedResources = useCallback(async () => {
        resourceCache(useGetConsumedResources).clear();
        await fetchNewConsumedResources({
            'pager.page': tabPagination.currentPage,
            'pager.itemsPerPage': tabPagination.pageSize,
            pagination: tabPagination,
            filter: {
                ...filter,
                id: warehouseId,
            },
        });
    }, [fetchNewConsumedResources, filter, tabPagination, warehouseId]);

    useEffect(() => {
        refetchRef.current = refetchConsumedResources;
        return () => {
            refetchRef.current = () => {
                resourceCache(useGetConsumedResources).clear();
            };
        };
    }, [refetchConsumedResources, refetchRef]);

    useEffect(() => {
        setCurrentPage(1);
        setPagination({ ...parentPagination });
    }, [parentPagination, setCurrentPage, setPagination]);

    return (
        <ConsumedResourcesTable
            getEntities={getConsumedResources}
            pagination={tabPagination}
            refetchEntitiesHook={refetchConsumedResources}
        />
    );
};

const CompletedHandoutsTabLoader = ({ filter, parentPagination, warehouseId, refetchRef }: WarehouseTabLoaderProps) => {
    const tabPagination = usePagination(parentPagination.pageSize);
    const { setCurrentPage, setPagination } = tabPagination;
    const [getCompletedHandouts, fetchNewCompletedHandouts] = useTableData<CompletedHandoutGroup[]>(
        useGetAllCompletedHandouts,
        { ...tabPagination, pageSize: parentPagination.pageSize },
        {
            ...filter,
            id: warehouseId,
        }
    );

    const refetchNewCompletedHandouts = useCallback(async () => {
        resourceCache(useGetAllCompletedHandouts).clear();
        await fetchNewCompletedHandouts({
            'pager.page': tabPagination.currentPage,
            'pager.itemsPerPage': tabPagination.pageSize,
            pagination: tabPagination,
            filter: {
                ...filter,
                id: warehouseId,
            },
        });
    }, [fetchNewCompletedHandouts, filter, tabPagination, warehouseId]);

    useEffect(() => {
        refetchRef.current = refetchNewCompletedHandouts;
        return () => {
            refetchRef.current = () => {
                resourceCache(useGetAllCompletedHandouts).clear();
            };
        };
    }, [refetchNewCompletedHandouts, refetchRef]);

    useEffect(() => {
        setCurrentPage(1);
        setPagination({ ...parentPagination });
    }, [parentPagination, setCurrentPage, setPagination]);

    return (
        <CompletedHandoutsTable
            getEntities={getCompletedHandouts}
            pagination={tabPagination}
            refetchEntitiesHook={refetchNewCompletedHandouts}
        />
    );
};

const AddMultiHandoutLoader = ({
    itemRelatedId,
    filter,
    parentPagination,
    setShowModal,
    refetchEntitiesHook,
}: {
    itemRelatedId?: number;
    filter: TableComponentProps['filter'];
    parentPagination: TableComponentProps['pagination'];
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetchEntitiesHook: () => void;
}) => {
    const tabPagination = usePagination(parentPagination.pageSize);
    const [getQuantities] = useTableData<ItemQuantity[]>(
        useGetAllQuantities,
        { ...tabPagination, pageSize: parentPagination.pageSize },
        {
            ...filter,
            id: String(itemRelatedId),
        }
    );

    return (
        <AddMultiHandout
            quantities={getQuantities()}
            setShowModal={setShowModal}
            itemRelatedId={itemRelatedId}
            refetchEntitiesHook={refetchEntitiesHook}
        />
    );
};

export const WarehouseView = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const params = useParams<WarehouseViewQueryParams>() as WarehouseViewQueryParams;
    const id = params.id;
    const [getWarehouse] = useAsyncResource(useGetWarehouse, id);
    const warehouse = getWarehouse();
    const isMainWarehouse = !warehouse.type;
    const warehouseSteps = isMainWarehouse ? ['quantities', 'consumedResources', 'completedHandouts'] : ['quantities'];

    const quantitiesRefetchRef = useRef<() => void>(() => {
        resourceCache(useGetAllQuantities).clear();
        resourceCache(getQuantityCount).clear();
        resourceCache(useGetPassportTypesForWarehouse).clear();
    });
    const consumedResourcesRefetchRef = useRef<() => void>(() => {
        resourceCache(useGetConsumedResources).clear();
    });
    const completedHandoutsRefetchRef = useRef<() => void>(() => {
        resourceCache(useGetAllCompletedHandouts).clear();
    });
    const [visitedTabs, setVisitedTabs] = useState<Set<number>>(new Set([1]));
    const visitedTabsRef = useRef<Set<number>>(new Set([1]));
    const activeStepRef = useRef(1);

    const refetchNewQuantities = () => quantitiesRefetchRef.current();
    const refetchNewCompletedHandouts = () => completedHandoutsRefetchRef.current();

    const refetchAfterPdfImport = async () => {
        quantitiesRefetchRef.current();
        consumedResourcesRefetchRef.current();
    };

    const refetchTab = useCallback((step: number) => {
        const refetchByStep: Record<number, () => void> = {
            1: quantitiesRefetchRef.current,
            2: consumedResourcesRefetchRef.current,
            3: completedHandoutsRefetchRef.current,
        };

        refetchByStep[step]?.();
    }, []);

    const handleStepChange = useCallback((stats: { activeStep: number }) => {
        if (activeStepRef.current === stats.activeStep) {
            return;
        }

        const nextVisitedTabs = new Set([...Array.from(visitedTabsRef.current), stats.activeStep]);

        if (visitedTabsRef.current.has(stats.activeStep)) {
            refetchTab(stats.activeStep);
        }

        activeStepRef.current = stats.activeStep;
        visitedTabsRef.current = nextVisitedTabs;
        setVisitedTabs(nextVisitedTabs);
    }, [refetchTab]);

    const [selectedItem, setSelectedItem] = useState<ItemQuantity>();
    const [showModal, setShowModal] = useState(false);
    const dragDepthRef = useRef(0);
    const dropZoneDragDepthRef = useRef(0);
    const importWarehousePdfFileRef = useRef<((file: File) => Promise<void>) | null>(null);
    const openWarehousePdfImportRef = useRef<(() => void) | null>(null);
    const [isFileDragging, setIsFileDragging] = useState(false);
    const [isDropZoneDragging, setIsDropZoneDragging] = useState(false);

    useEffect(() => {
        if (!isMainWarehouse) {
            return;
        }

        const isFileDrag = (event: globalThis.DragEvent) => {
            return Array.from(event.dataTransfer?.types || []).includes('Files');
        };

        const handleDragEnter = (event: globalThis.DragEvent) => {
            if (!isFileDrag(event)) {
                return;
            }

            event.preventDefault();
            dragDepthRef.current += 1;
            setIsFileDragging(true);
        };

        const handleDragOver = (event: globalThis.DragEvent) => {
            if (!isFileDrag(event)) {
                return;
            }

            event.preventDefault();
            setIsFileDragging(true);
        };

        const handleDragLeave = (event: globalThis.DragEvent) => {
            if (!isFileDrag(event)) {
                return;
            }

            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

            if (dragDepthRef.current === 0) {
                setIsFileDragging(false);
            }
        };

        const handleDrop = () => {
            dragDepthRef.current = 0;
            dropZoneDragDepthRef.current = 0;
            setIsFileDragging(false);
            setIsDropZoneDragging(false);
        };

        window.addEventListener('dragenter', handleDragEnter);
        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('dragleave', handleDragLeave);
        window.addEventListener('drop', handleDrop);

        return () => {
            window.removeEventListener('dragenter', handleDragEnter);
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('dragleave', handleDragLeave);
            window.removeEventListener('drop', handleDrop);
        };
    }, [isMainWarehouse]);

    const handleWarehouseNameDragOver = (event: React.DragEvent<HTMLElement>) => {
        if (!isMainWarehouse) {
            return;
        }

        if (!Array.from(event.dataTransfer.types || []).includes('Files')) {
            return;
        }

        event.preventDefault();
        setIsDropZoneDragging(true);
    };

    const handleWarehouseNameDragEnter = (event: React.DragEvent<HTMLElement>) => {
        if (!isMainWarehouse) {
            return;
        }

        if (!Array.from(event.dataTransfer.types || []).includes('Files')) {
            return;
        }

        event.preventDefault();
        dropZoneDragDepthRef.current += 1;
        setIsDropZoneDragging(true);
    };

    const handleWarehouseNameDragLeave = (event: React.DragEvent<HTMLElement>) => {
        if (!isMainWarehouse) {
            return;
        }

        if (!Array.from(event.dataTransfer.types || []).includes('Files')) {
            return;
        }

        dropZoneDragDepthRef.current = Math.max(0, dropZoneDragDepthRef.current - 1);

        if (dropZoneDragDepthRef.current === 0) {
            setIsDropZoneDragging(false);
        }
    };

    const handleWarehouseNameDrop = async (event: React.DragEvent<HTMLElement>) => {
        if (!isMainWarehouse) {
            return;
        }

        if (!Array.from(event.dataTransfer.types || []).includes('Files')) {
            return;
        }

        event.preventDefault();
        dragDepthRef.current = 0;
        dropZoneDragDepthRef.current = 0;
        setIsFileDragging(false);
        setIsDropZoneDragging(false);

        const file = event.dataTransfer.files?.[0];

        if (file) {
            await importWarehousePdfFileRef.current?.(file);
        }
    };

    return (
        <div className='w-100'>
            {isMainWarehouse && selectedItem && (
                <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' centered>
                    <Modal.Header closeButton>
                        <Modal.Title id='contained-modal-title-vcenter'>{t('quantity')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <AddEditQuantity
                            passport={selectedItem.passport}
                            setShowModal={setShowModal}
                            itemRelatedId={selectedItem?.id}
                            refetchEntitiesHook={refetchNewQuantities}
                            warehouseId={+params.id}
                        />
                    </Modal.Body>
                    <Modal.Footer className='d-flex justify-content-between'>
                        <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                        <Button form='form' type='submit' className='btn-success'>
                            {t('add')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
            <div className='w-100 d-flex align-items-center justify-content-center mb-3' style={{ height: '110px' }}>
                {!isMainWarehouse ? (
                    <h3 className='text-center mb-0 d-inline-block px-4' style={{ fontSize: '34px', lineHeight: '70px' }}>
                        {warehouse.warehouseName}
                    </h3>
                ) : isFileDragging ? (
                    <div
                        className='d-inline-flex flex-column align-items-center text-center rounded px-4 py-2'
                        onDragEnter={handleWarehouseNameDragEnter}
                        onDragOver={handleWarehouseNameDragOver}
                        onDragLeave={handleWarehouseNameDragLeave}
                        onDrop={handleWarehouseNameDrop}
                        style={{
                            backgroundColor: isDropZoneDragging ? '#9fe9aa' : '#c8f3ce',
                            maxWidth: '100%',
                            transition: 'background-color 120ms ease-in-out',
                        }}
                    >
                        <h3 className='mb-1' style={{ fontSize: '10px', lineHeight: '14px' }}>{warehouse.warehouseName}</h3>
                        <FontAwesomeIcon icon={faCloudUploadAlt} size='3x' />
                        <p className='mb-0 mt-1 fw-bold'>{t('warehousePdfImportDropHere')}</p>
                    </div>
                ) : (
                    <OverlayTrigger
                        placement='bottom'
                        overlay={
                            <Tooltip id='warehouse-name-upload-tooltip'>
                                <FontAwesomeIcon icon={faCloudUploadAlt} className='me-1' />
                                {t('warehousePdfImportNameTooltip')}
                            </Tooltip>
                        }
                    >
                        <h3
                            className='text-center mb-0 d-inline-block px-4'
                            onClick={() => openWarehousePdfImportRef.current?.()}
                            onDragOver={handleWarehouseNameDragOver}
                            onDrop={handleWarehouseNameDrop}
                            style={{ cursor: 'pointer', fontSize: '34px', lineHeight: '70px' }}
                        >
                            {warehouse.warehouseName}
                            <FontAwesomeIcon
                                icon={faCloudUploadAlt}
                                className='d-block mx-auto text-secondary'
                                style={{ fontSize: '20px', marginTop: '-0.85rem' }}
                            />
                        </h3>
                    </OverlayTrigger>
                )}
            </div>
            {isMainWarehouse && (
                <div className='d-flex w-auto align-self-start mb-3 gap-3'>
                    <ManualWarehouseQuantityImport warehouseId={+params.id} onImportSuccess={refetchAfterPdfImport} />
                    <AddActionComponent
                        wrapperClassName='w-auto'
                        size='xl'
                        customClasses='multi-hangouts-custom'
                        dialogClassName='handout-modal-dialog'
                        centered={false}
                        addComponent={({ setShowModal, itemRelatedId, refetchEntitiesHook }) => (
                            <Suspense fallback={<Spinner animation='border' />}>
                                <AddMultiHandoutLoader
                                    filter={filter}
                                    parentPagination={pagination}
                                    setShowModal={setShowModal}
                                    itemRelatedId={itemRelatedId}
                                    refetchEntitiesHook={refetchEntitiesHook}
                                />
                            </Suspense>
                        )}
                        accessActionRoles={[Role.WAREHOUSE]}
                        title={t('addHandout')}
                        itemRelatedId={+params.id}
                        refetchEntitiesHook={refetchNewCompletedHandouts}
                    />
                    <WarehousePdfImport
                        buttonClass=''
                        hideButton
                        importFileRef={importWarehousePdfFileRef}
                        openFileDialogRef={openWarehousePdfImportRef}
                        warehouseId={+params.id}
                        onImportSuccess={refetchAfterPdfImport}
                    />
                </div>
            )}
            <StepWizard
                className='full-width-table d-flex flex-wrap w-100'
                nav={<CustomStepWizardNav steps={warehouseSteps} />}
                transitions={{
                    enterRight: '',
                    enterLeft: '',
                    exitRight: '',
                    exitLeft: '',
                }}
                onStepChange={handleStepChange}
            >
                {[
                    <div key='quantities' className='w-100'>
                        {visitedTabs.has(1) && (
                            <Suspense fallback={<Spinner animation='border' />}>
                                <QuantitiesTabLoader
                                    filter={filter}
                                    parentPagination={pagination}
                                    warehouseId={id ?? ''}
                                    warehouseType={warehouse.type}
                                    refetchRef={quantitiesRefetchRef}
                                    setShowModal={setShowModal}
                                    setSelectedItem={setSelectedItem}
                                />
                            </Suspense>
                        )}
                    </div>,
                    ...(isMainWarehouse
                        ? [
                              <div key='consumed' className='w-100'>
                                  {visitedTabs.has(2) && (
                                      <Suspense fallback={<Spinner animation='border' />}>
                                          <ConsumedResourcesTabLoader
                                              filter={filter}
                                              parentPagination={pagination}
                                              warehouseId={id ?? ''}
                                              refetchRef={consumedResourcesRefetchRef}
                                          />
                                      </Suspense>
                                  )}
                              </div>,
                              <div key='completed' className='w-100'>
                                  {visitedTabs.has(3) && (
                                      <Suspense fallback={<Spinner animation='border' />}>
                                          <CompletedHandoutsTabLoader
                                              filter={filter}
                                              parentPagination={pagination}
                                              warehouseId={id ?? ''}
                                              refetchRef={completedHandoutsRefetchRef}
                                          />
                                      </Suspense>
                                  )}
                              </div>,
                          ]
                        : []),
                ]}
            </StepWizard>
        </div>
    );
};
